import {useState} from "react";
import {useToast} from "@chakra-ui/react";
import {getUploadLink} from "../services/projectService";
import Uppy, {UppyFile} from "@uppy/core";
import XHR from "@uppy/xhr-upload";
import {getFileBaseName} from "../utils/utils.ts";
import iso6391 from "iso-639-1";
import {ALLOWED_TYPES, GLIX_SHOT_DETECTION_SENSITIVITY_DEFAULT} from "../models/constants/constants";
import {useProjectsStore} from "../store/useProjectsStore";
import {IUploadItem} from "../models/interfaces/uploadItem.interface";
import {ICreateNewProjectState} from "../reducers/createNewProjectReducer.ts";
import {INewProject} from "../models/interfaces/newProject.interface.ts";
import {IUploadLink} from "../models/interfaces/uploadLink.interface.ts";
import {FileProgressStarted} from "@uppy/utils/lib/FileProgress";
import {NewProjectConfigurationConfig} from "../models/interfaces/newProjectConfigurations.ts";
import useUserStore from "../store/useUserStore.ts";
import {GranularityEnum} from "../models/enums/granularity.enum.ts";

export const useProjectUploader = () => {
  const [uploads, setUploads] = useState<IUploadItem[]>([]);
  const {addNewProjectCreationConfiguration} = useUserStore();
  const toast = useToast();
  const {createProject} = useProjectsStore();

  const triggerUpload: (state: ICreateNewProjectState) => Promise<void> = async (state: ICreateNewProjectState): Promise<void> => {
    const {attachedFiles, customConfigurationName, subtitles, metadata, advanced, fileNameToSeriesNameRelation} = state;

    try {
      const configuration: NewProjectConfigurationConfig = {
        sourceTranscriptionConfig: Object.values(subtitles.sourceLanguage)[0],
        subtitles: subtitles.isTranscribeOnly
          ? {}
          : Object.fromEntries(
            Object.entries(subtitles.targetLanguages).map(([lang, config]) => [
              iso6391.getCode(lang),
              config,
            ])
          ),
        shotChangeSync: subtitles.features?.shotChangeSync ?? false,
        dialogueListConfig: metadata.isDialogueListSelected
          ? {
            ...(state.metadata.isExtendedMetadata?.isSelected && {
              extendedMetadata: {
                granularity: state.metadata.isExtendedMetadata.granularity,
                shotChangeDetectionSensitivity: state.metadata.isExtendedMetadata.granularity === GranularityEnum.GLIX ? state.metadata.isExtendedMetadata.shotChangeDetectionSensitivity ?? GLIX_SHOT_DETECTION_SENSITIVITY_DEFAULT : undefined,
                visualClassifiers: state.metadata.isExtendedMetadata.visualTags,
              },
            }),
          }
          : undefined,
        audioDescriptions: advanced.isSelected
          ? {
            targetLanguages: (state.advanced.audioDescription ?? []).map(
              (lang: string) => iso6391.getCode(lang)
            ),
          }
          : undefined,
      }
      const sourceLanguageCode: string = iso6391.getCode(Object.keys(subtitles.sourceLanguage)[0])
      if (customConfigurationName) {
        await addNewProjectCreationConfiguration(customConfigurationName, sourceLanguageCode, configuration);
        toast({
          position: "bottom-left",
          title: "Configuration created",
          description: "Your custom configuration has been created successfully.",
          status: "success",
          duration: 2000,
          isClosable: true,
        });

      }
      const uploadLinkResponse: IUploadLink[] = await getUploadLink(
        ALLOWED_TYPES[attachedFiles[0].type],
        attachedFiles.length
      );

      setUploads((prev: IUploadItem[]) => [
        ...prev,
        ...attachedFiles.map((file: File) => ({
          fileName: file.name,
          progress: 0,
          status: "Uploading",
        })),
      ]);

      const uploadPromises: Promise<void>[] = attachedFiles.map((file: File, i: number) => {
        const {uploadUrl, uploadId} = uploadLinkResponse[i];
        const endpoint: string = `${uploadUrl.url}?${uploadUrl.sasToken}`;

        return new Promise<void>((resolve, reject) => {
          const uppy = new Uppy();
          uppy.addFile({name: file.name, type: file.type, data: file});

          uppy.use(XHR, {
            method: "PUT",
            formData: false,
            bundle: false,
            endpoint,
            headers: {
              "x-ms-blob-type": "BlockBlob",
              "x-ms-blob-content-type": file.type,
            },
          });

          uppy.on(
            "upload-progress",
            (
              fileObj: UppyFile<Record<string, unknown>, Record<string, unknown>> | undefined,
              progress: FileProgressStarted
            ) => {
              if (!fileObj) return;
              const percentage: number = Math.round(
                (progress.bytesUploaded / (progress.bytesTotal ?? 1)) * 100
              );
              setUploads((prev: IUploadItem[]) =>
                prev.map((u: IUploadItem) =>
                  u.fileName === fileObj.name ? {...u, progress: percentage} : u
                )
              );
            }
          );

          (async () => {
            try {
              await uppy.upload();
              setUploads((prev: IUploadItem[]) =>
                prev.map((u: IUploadItem) =>
                  u.fileName === file.name
                    ? {...u, status: "success", progress: 100}
                    : u
                )
              );

              const newProject: INewProject = {
                name: getFileBaseName(file.name),
                seriesName: fileNameToSeriesNameRelation[file.name] || undefined,
                uploadId: uploadId,
                sourceLanguage: sourceLanguageCode,
                features: configuration,
              };

              await createProject(newProject);

              setUploads((prev: IUploadItem[]) =>
                prev.filter((u: IUploadItem) => u.fileName !== file.name)
              );
              resolve();
            } catch (err) {
              reject(err);
            }
          })();
        });
      });

      await Promise.all(uploadPromises);
    } catch (error) {
      console.error("Upload failed:", error);
      toast({
        position: "bottom-left",
        title: "Upload failed",
        description: "Something went wrong. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return {uploads, triggerUpload};
};